import { zodResolver } from "@hookform/resolvers/zod";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { AxiosError } from "axios";
import { KeyRound, QrCode } from "lucide-react";
import React, { Suspense, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { SSO_LOGIN_URL } from "@/api/auth";
import { ssoErrorText } from "@/auth/sso-messages";
import { extractErrorMessage } from "@/lib/api-errors";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/useAuth";
import { usePortalConfig } from "@/portal/usePortalConfig";

// Кладовщик заходит с телефона и с ТСД, и логин с паролем набирает в перчатках.
// Камера — та же, что у скана товара (подключается через `lazy-camera`).

// Пропуск печатается в QR, а сканер по умолчанию ищет товарные коды склада
// (DataMatrix ЧЗ и EAN13) — QR он просто не видит, и камера «не реагирует».
// Набор общий (`CameraScanner`), а не свой: та же грабля повторилась на
// проверке отгрузки, потому что список пришлось бы вспомнить заново.
import { QR_FORMATS as BADGE_FORMATS } from "@/lib/scan-formats";

const schema = z.object({
  username: z.string().min(1, "Введите логин"),
  password: z.string().min(1, "Введите пароль"),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const { user, login, loginByBadge, loading, restoreError } = useAuth();
  const { title, badge_prefix: badgePrefix, sso_enabled: ssoEnabled } = usePortalConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);

  // Возврат из SSO: бэкенд уводит сюда с `?sso=ok` или `?sso_error=<код>`.
  // На `sso=ok` refresh-cookie уже стоит, и сессию поднимает ОБЫЧНОЕ
  // восстановление при загрузке (`AuthContext` → `/refresh`). Второй
  // `/refresh` отсюда не делаем: два параллельных обмена одной cookie —
  // это повтор отозванного токена, и бэкенд отзывает ВСЕ сессии человека.
  // Вошли → `<Navigate>` ниже уводит дальше; не вошли → показываем причину.
  // Параметр из адреса убираем, чтобы перезагрузка не повторяла сообщение.
  const ssoResult = searchParams.get("sso");
  const ssoErrorCode = searchParams.get("sso_error");
  useEffect(() => {
    if (ssoResult === null && ssoErrorCode === null) return;
    if (loading || user) return;
    if (ssoErrorCode !== null) {
      setServerError(ssoErrorText(ssoErrorCode));
    } else if (ssoResult === "ok") {
      setServerError(
        `Вход через SSO не завершён: ${extractErrorMessage(
          restoreError,
          "сессия портала не получена",
        )}. Попробуйте ещё раз.`,
      );
    } else {
      setServerError(`Вход через SSO: неизвестный ответ «${ssoResult}».`);
    }
    const next = new window.URLSearchParams(searchParams);
    next.delete("sso");
    next.delete("sso_error");
    setSearchParams(next, { replace: true, state: location.state });
  }, [
    ssoResult,
    ssoErrorCode,
    loading,
    user,
    restoreError,
    searchParams,
    setSearchParams,
    location.state,
  ]);
  const [scanning, setScanning] = useState(false);
  const [badgeBusy, setBadgeBusy] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Пропуск, отсканированный СКАНЕРОМ ТСД. Сканер работает как клавиатура и
  // печатает код в то поле, где стоит курсор, — на этом экране это логин.
  // Отдельного «режима скана» тут нет намеренно: кладовщик в перчатках просто
  // подносит пропуск, и требовать от него сперва нажать кнопку значит
  // вернуть ровно ту возню, ради ухода от которой пропуск и заведён.
  //
  // Опознаём по префиксу: ничего, кроме пропуска, с него начинаться не может,
  // поэтому обычный ввод логина не задевается.
  const badgeTimer = React.useRef<number | undefined>(undefined);
  const lastFired = React.useRef("");

  if (loading) return <div className="p-8 text-center">Загрузка…</div>;
  if (user) {
    const redirectTo = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={redirectTo} replace />;
  }

  // Причина отказа одна на оба входа: 429 и текст сервера читаются одинаково,
  // а второй разбор ошибок разъехался бы с первым на первой же правке.
  const showError = (err: unknown) => {
    if (err instanceof AxiosError) {
      const detail = err.response?.data?.detail;
      setServerError(
        err.response?.status === 429
          ? "Слишком много попыток. Попробуйте позже."
          : typeof detail === "string"
            ? detail
            : "Ошибка входа",
      );
    } else {
      setServerError("Ошибка входа");
    }
  };

  const goHome = () => {
    const redirectTo = (location.state as { from?: string } | null)?.from ?? "/";
    navigate(redirectTo, { replace: true });
  };

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.username, values.password);
      goHome();
    } catch (err) {
      showError(err);
    }
  };

  /** Пропуск дошёл целиком — входим и чистим форму от служебной строки. */
  const fireBadge = (raw: string): boolean => {
    const code = raw.trim();
    if (!code.startsWith(badgePrefix)) return false;
    window.clearTimeout(badgeTimer.current);
    // Сканер шлёт и суффикс Enter, и паузу — без защиты один пропуск ушёл бы
    // на сервер дважды и сжёг бы две попытки из десяти в rate-limit.
    if (lastFired.current === code) return true;
    lastFired.current = code;
    reset({ username: "", password: "" });
    void onBadge(code).finally(() => {
      // Отпускаем через секунду: повторное поднесение того же пропуска —
      // нормальный способ повторить вход после ошибки.
      window.setTimeout(() => {
        lastFired.current = "";
      }, 1000);
    });
    return true;
  };

  /** Пропсы поля, в которое может прилететь скан. Обработчики react-hook-form
   *  вызываем ПЕРВЫМИ — иначе форма не узнает о вводе. */
  const scannable = (field: "username" | "password") => {
    const reg = register(field);
    return {
      ...reg,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        void reg.onChange(e);
        const value = e.target.value;
        window.clearTimeout(badgeTimer.current);
        if (!value.trim().startsWith(badgePrefix)) return;
        // Сканер без суффикса — добиваем паузой, как в общем перехвате скана.
        badgeTimer.current = window.setTimeout(() => fireBadge(value), 250);
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        if (fireBadge(e.currentTarget.value)) e.preventDefault();
      },
    };
  };

  const onBadge = async (code: string) => {
    setScanning(false);
    setServerError(null);
    setBadgeBusy(true);
    try {
      await loginByBadge(code);
      goHome();
    } catch (err) {
      showError(err);
    } finally {
      setBadgeBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Логин</Label>
              <Input id="username" autoComplete="username" autoFocus {...scannable("username")} />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...scannable("password")}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            {serverError && <p className="text-sm text-destructive">{serverError}</p>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Входим…" : "Войти"}
            </Button>
          </form>

          <Button
            type="button"
            variant="outline"
            className="mt-3 h-12 w-full"
            disabled={badgeBusy}
            onClick={() => {
              setServerError(null);
              setScanning(true);
            }}
          >
            <QrCode className="mr-2 size-5" />
            {badgeBusy ? "Входим…" : "Вход по пропуску"}
          </Button>

          {/* Про сканер говорим прямо: кнопка рядом выглядит как ЕДИНСТВЕННЫЙ
              способ, и человек с ТСД в руках жмёт её вместо того, чтобы
              просто выстрелить в пропуск. */}
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Со сканером ТСД кнопка не нужна — просто отсканируйте пропуск.
          </p>

          {/* SSO — в самом низу: экран входа ТСД (логин, пропуск) не должен
              сдвигаться ради кнопки, которой на складе не пользуются.
              Ссылка, а не fetch: вход — полная навигация браузера через
              Zitadel и обратно. */}
          {ssoEnabled && (
            <Button asChild variant="outline" className="mt-4 h-12 w-full">
              <a href={SSO_LOGIN_URL}>
                <KeyRound className="mr-2 size-5" />
                Войти через SSO
              </a>
            </Button>
          )}
        </CardContent>
      </Card>

      {scanning && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-50 bg-black">
            <CameraScanner
              onDetect={onBadge}
              onClose={() => setScanning(false)}
              formats={BADGE_FORMATS}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}
